import { useCallback, useEffect, useRef } from 'react';
import type { EditorHandle } from '../components/Editor';
import { useStore } from '../store/useStore';
import {
  bootstrapLibrary,
  createDocument,
  deleteDocument,
  duplicateDocument,
  getDocument,
  getSession,
  listDocuments,
  saveDocument,
  saveSession,
  setLastOpenDocId,
} from '../lib/library';
import { consumeInboundDoc } from '../lib/inbound';
import { consumeSharedDocs } from '../lib/shareTarget';
// Static, not `await import()`: both modules are tiny and already reach the
// main chunk via other static importers, so a dynamic import only bought a
// Rollup warning and an extra async hop.
import { maybeSnapshot } from '../lib/snapshots';
import { importMarkdownFiles, type ImportItem } from '../lib/import';
import { startSearchIndex } from '../lib/searchClient';

const AUTOSAVE_MS = 400;
const SESSION_SAVE_MS = 500;

export interface DocController {
  open: (id: string) => Promise<void>;
  create: () => Promise<void>;
  /** Create a new document seeded with the given content (templates). */
  createFrom: (content: string) => Promise<void>;
  duplicate: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** Restore a soft-deleted document and open it. */
  restore: (id: string) => Promise<void>;
  /** Import Markdown files into the library and open the last one. */
  /** Resolves with the names of files that could not be converted. */
  importFiles: (files: FileList | ReadonlyArray<File | ImportItem>) => Promise<string[]>;
  /** Persist the active doc immediately (used before navigating away). */
  flush: () => Promise<void>;
  /** Debounced save of cursor/scroll/view-mode session; call on editor activity. */
  persistSession: () => void;
}

/**
 * Owns the lifecycle glue between the Zustand store and the Dexie library:
 * bootstrap on mount, debounced content autosave, debounced session save
 * (cursor/scroll/view mode), and document switching with full state restore.
 */
export function useDocController(editorRef: React.RefObject<EditorHandle>): DocController {
  const autosaveTimer = useRef<number | null>(null);
  const sessionTimer = useRef<number | null>(null);
  const restorePending = useRef(false);

  // ---- content autosave: watch active doc content/title ----
  useEffect(() => {
    return useStore.subscribe((state, prev) => {
      const doc = state.activeDoc;
      if (!doc.id) return;
      if (doc.content === prev.activeDoc.content && doc.title === prev.activeDoc.title) return;
      if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      useStore.getState().setSaveState(offline ? 'offline' : 'saving');
      autosaveTimer.current = window.setTimeout(() => {
        void saveDocument(doc.id, { content: doc.content, title: doc.title }).then(async () => {
          const nowOffline = typeof navigator !== 'undefined' && !navigator.onLine;
          useStore.getState().setSaveState(nowOffline ? 'offline' : 'saved');
          // Periodic version snapshot (throttled inside maybeSnapshot).
          await maybeSnapshot({ id: doc.id, title: doc.title, content: doc.content });
        });
      }, AUTOSAVE_MS);
    });
  }, []);

  // Reflect connectivity in the save indicator (docs still save locally offline).
  useEffect(() => {
    const update = () => useStore.getState().setSaveState(navigator.onLine ? 'saved' : 'offline');
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  // ---- session save: watch view mode / switch pane ----
  const persistSession = useCallback(() => {
    if (sessionTimer.current) window.clearTimeout(sessionTimer.current);
    sessionTimer.current = window.setTimeout(() => {
      const { activeDoc, viewMode, switchPane } = useStore.getState();
      if (!activeDoc.id || restorePending.current) return;
      const sel = editorRef.current?.getSelection() ?? { anchor: 0, head: 0 };
      const scrollTop = editorRef.current?.getScrollTop() ?? 0;
      void saveSession({ docId: activeDoc.id, cursor: sel, scrollTop, viewMode, switchPane });
    }, SESSION_SAVE_MS);
  }, [editorRef]);

  useEffect(() => {
    return useStore.subscribe((state, prev) => {
      if (state.viewMode !== prev.viewMode || state.switchPane !== prev.switchPane) {
        persistSession();
      }
    });
  }, [persistSession]);

  const flush = useCallback(async () => {
    const pending = autosaveTimer.current !== null;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = null;
    const doc = useStore.getState().activeDoc;
    if (!doc.id) return;
    await saveDocument(doc.id, { content: doc.content, title: doc.title });
    // Cancelling the pending autosave also cancelled the only code that set the
    // indicator back to "saved", so a flush mid-edit left it on "Saving…" until
    // the next keystroke. Report the save that just happened instead.
    if (pending) {
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      useStore.getState().setSaveState(offline ? 'offline' : 'saved');
    }
  }, []);

  const applyDoc = useCallback(
    async (id: string) => {
      const [doc, session] = await Promise.all([getDocument(id), getSession(id)]);
      if (!doc) return;
      restorePending.current = true;
      useStore
        .getState()
        .loadDoc({ id: doc.id, title: doc.title, content: doc.content }, session ?? null);
      useStore.getState().addTab(doc.id);
      await setLastOpenDocId(doc.id);
      // Restore cursor/scroll after the editor reconciles the new content.
      window.setTimeout(() => {
        const s = useStore.getState().consumePendingSession();
        if (s && editorRef.current) {
          editorRef.current.setSelection(s.cursor.anchor, s.cursor.head);
          editorRef.current.setScrollTop(s.scrollTop);
        }
        restorePending.current = false;
      }, 0);
    },
    [editorRef],
  );

  const open = useCallback(
    async (id: string) => {
      await flush();
      await applyDoc(id);
    },
    [flush, applyDoc],
  );

  const create = useCallback(async () => {
    await flush();
    const doc = await createDocument({ content: '' });
    await applyDoc(doc.id);
  }, [flush, applyDoc]);

  const createFrom = useCallback(
    async (content: string) => {
      await flush();
      const doc = await createDocument({ content });
      await applyDoc(doc.id);
    },
    [flush, applyDoc],
  );

  const importFiles = useCallback(
    async (files: FileList | ReadonlyArray<File | ImportItem>) => {
      await flush();
      const { ids, failed } = await importMarkdownFiles(files);
      const last = ids[ids.length - 1];
      if (last) await applyDoc(last);
      return failed;
    },
    [flush, applyDoc],
  );

  const duplicate = useCallback(
    async (id: string) => {
      await flush();
      const doc = await duplicateDocument(id);
      if (doc) await applyDoc(doc.id);
    },
    [flush, applyDoc],
  );

  const remove = useCallback(
    async (id: string) => {
      const doc = await getDocument(id);
      await deleteDocument(id); // soft-delete (restorable)
      useStore.getState().closeTab(id);
      useStore.getState().setUndoDelete({ docId: id, title: doc?.title || 'Untitled' });
      const current = useStore.getState().activeDoc.id;
      if (current === id) {
        const remaining = await listDocuments();
        if (remaining[0]) {
          await applyDoc(remaining[0].id);
        } else {
          const fresh = await createDocument({ content: '' });
          await applyDoc(fresh.id);
        }
      }
    },
    [applyDoc],
  );

  const restore = useCallback(
    async (id: string) => {
      const { restoreDocument } = await import('../lib/library');
      await restoreDocument(id);
      useStore.getState().setUndoDelete(null);
      await applyDoc(id);
    },
    [applyDoc],
  );

  // ---- bootstrap once on mount ----
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    void (async () => {
      // bootstrapLibrary() still runs for its index warm-up and first-run
      // welcome doc; an inbound document only decides *which* doc we open. The
      // two stay in one await chain so there is exactly one applyDoc() — a
      // second one racing this is what used to clobber freshly created docs.
      const doc = await bootstrapLibrary();
      const inbound = await consumeInboundDoc();
      const shared = await consumeSharedDocs();
      await applyDoc((shared ?? inbound ?? doc).id);
      // Warm the search index once the editor is usable. It builds in idle-time
      // chunks, so this costs the first search its wait rather than the boot.
      void startSearchIndex();
    })();
  }, [applyDoc]);

  // ---- live-update: let the sync engine refresh the open doc in place ----
  useEffect(() => {
    const reload = (id: string) => {
      if (id === useStore.getState().activeDoc.id) void applyDoc(id);
    };
    useStore.getState().setReloadOpenDoc(reload);
    return () => useStore.getState().setReloadOpenDoc(null);
  }, [applyDoc]);

  return {
    open,
    create,
    createFrom,
    duplicate,
    remove,
    restore,
    importFiles,
    flush,
    persistSession,
  };
}
